import type { RouteLocationNormalized } from "vue-router";
import { canPerformAction, type PermissionsMap } from "@parkingmgt/iam-sdk-ts";
import { getAvailableReports } from "@/helpers/reporting";
import { useAppUserStore } from "@/stores/app-user";
import {
  USE_REVENUE_DASHBOARD,
  USE_FRONTDESK_DASHBOARD,
  USE_EVENT_DASHBOARD,
  USE_OCCUPANCY_DASHBOARD,
  USE_VALIDATION_DASHBOARD,
  USE_VALETINSIGHTS_DASHBOARD,
  USE_DAILY_LOCATION_SUMMARY_DASHBOARD,
} from "@/helpers/constants";

// Map of permission to route name
const DASHBOARD_PERMISSIONS = [
  { permission: USE_EVENT_DASHBOARD, route: "Event" },
  { permission: USE_REVENUE_DASHBOARD, route: "Dashboard" },
  { permission: USE_OCCUPANCY_DASHBOARD, route: "Occupancy" },
  { permission: USE_FRONTDESK_DASHBOARD, route: "FrontDesk" },
  { permission: USE_VALIDATION_DASHBOARD, route: "Validation" },
  { permission: USE_VALETINSIGHTS_DASHBOARD, route: "ValetInsights" },
  {
    permission: USE_DAILY_LOCATION_SUMMARY_DASHBOARD,
    route: "DailyLocationSummary",
  },
];

function getFirstAvailableDashboard(
  permissions: PermissionsMap,
  hasEventAccess: boolean,
): string | null {
  // Check priority dashboards first (Event, then Revenue/Dashboard).
  // Event is venue-scoped and does NOT honor the partnerPortal:* wildcard —
  // see hasEventAccess in app-user.ts.
  if (hasEventAccess) {
    return "Event";
  }
  if (canPerformAction(USE_REVENUE_DASHBOARD, permissions)) {
    return "Dashboard";
  }

  // Then check any other available dashboard
  for (const { permission, route } of DASHBOARD_PERMISSIONS) {
    if (permission === USE_EVENT_DASHBOARD) continue; // handled above
    if (canPerformAction(permission, permissions)) {
      return route;
    }
  }

  return null;
}

export default function permissionsGuard(to: RouteLocationNormalized) {
  const appUserStore = useAppUserStore();

  if (!appUserStore.user?.permissions) {
    return { name: "Login" };
  }

  const permissions = appUserStore.user
    .permissions as unknown as PermissionsMap;

  switch (to.name) {
    case "Dashboard":
      if (!canPerformAction(USE_REVENUE_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "FrontDesk":
    case "StartParkingSession":
      if (!canPerformAction(USE_FRONTDESK_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "Occupancy":
      if (!canPerformAction(USE_OCCUPANCY_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "Reporting":
      if (!getAvailableReports()?.length) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "Event":
      if (!appUserStore.hasEventAccess) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "Validation":
      if (!canPerformAction(USE_VALIDATION_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "ValetInsights":
      if (!canPerformAction(USE_VALETINSIGHTS_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;

    case "DailyLocationSummary":
      if (!canPerformAction(USE_DAILY_LOCATION_SUMMARY_DASHBOARD, permissions)) {
        const fallbackDashboard = getFirstAvailableDashboard(permissions, appUserStore.hasEventAccess);
        return fallbackDashboard ? { name: fallbackDashboard } : { name: "Login" };
      }
      break;
  }
}